import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Alert, Button, Form, FormText, Input, Label } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamErrorMessage } from '../team/teamErrorMessage';
import { useRenameWorkspace } from './useRenameWorkspace';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

// The limit of `workspaces.rename` (music24-api): 80 characters after the trim.
const MAX_NAME_LENGTH = 80;

interface WorkspaceNameFormProps {
  /** `team.list.customWorkspaceName`: the name the owner typed. Null means the default name. */
  customName: string | null;
  /** The name an empty field goes back to (first artist, else the owner email). Null while it loads. */
  defaultName: string | null;
}

/**
 * design: workspaces d1 / d2. The owner names the active workspace. The app counts the characters
 * after the trim. More than 80: Save is off and the hint is red. An empty name goes back to the
 * default name, which the empty field shows as its placeholder. After a save, Save stays off until the text changes.
 */
const WorkspaceNameFormComponent: React.FC<WorkspaceNameFormProps> = ({ customName, defaultName }) => {
  const intl = useIntl();
  const { rename, isPending, error, isSaved } = useRenameWorkspace();
  const [value, setValue] = useState(customName ?? '');
  // A rename in another tab, then a team.list refetch, gives a new `customName`. The field follows
  // it when it has no edits (React: adjust the state while rendering).
  const [shownName, setShownName] = useState(customName);
  if (customName !== shownName) {
    setShownName(customName);
    if (value === (shownName ?? '')) {
      setValue(customName ?? '');
    }
  }
  const [savedName, setSavedName] = useState<string | null>(null);
  const trimmed = value.trim();
  const isTooLong = trimmed.length > MAX_NAME_LENGTH;
  const showSaved = isSaved && savedName === trimmed;

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (isTooLong) {
        return;
      }
      setSavedName(trimmed);
      rename(trimmed).catch(() => undefined);
    },
    [isTooLong, rename, trimmed]
  );

  return (
    <Panel as="section" className="mb-4">
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.workspaces.rename.title', defaultMessage: 'Workspace' })}
        right={intl.formatMessage({ id: 'app.sml.team.role.owner', defaultMessage: 'Owner' })}
      />
      <PanelBody>
        <Form onSubmit={submit}>
          <div className="d-flex align-items-baseline justify-content-between mb-2">
            <Label for="workspace-name" className="mb-0">
              {intl.formatMessage({ id: 'app.sml.workspaces.rename.label', defaultMessage: 'Workspace name' })}
            </Label>
            <small className={clsx(isTooLong ? 'text-danger' : 'text-muted')}>
              {intl.formatMessage(
                { id: 'app.sml.workspaces.rename.count', defaultMessage: '{count} / {max}' },
                { count: trimmed.length, max: MAX_NAME_LENGTH }
              )}
            </small>
          </div>
          <div className="d-flex gap-2">
            <Input
              id="workspace-name"
              value={value}
              invalid={isTooLong}
              placeholder={defaultName ?? undefined}
              onChange={(event) => setValue(event.target.value)}
            />
            <Button color="primary" type="submit" disabled={isPending || isTooLong || showSaved}>
              {intl.formatMessage({ id: 'app.sml.workspaces.rename.save', defaultMessage: 'Save' })}
            </Button>
          </div>
          <FormText color={isTooLong ? 'danger' : 'muted'} className="d-block mt-2">
            {isTooLong
              ? intl.formatMessage({
                  id: 'app.sml.workspaces.rename.tooLong',
                  defaultMessage: 'Use 80 characters or fewer.',
                })
              : defaultName !== null && trimmed === ''
                ? intl.formatMessage(
                    {
                      id: 'app.sml.workspaces.rename.helpEmpty',
                      defaultMessage: 'Save with an empty name to go back to the default name, {name}.',
                    },
                    { name: defaultName }
                  )
                : defaultName !== null
                  ? intl.formatMessage(
                      {
                        id: 'app.sml.workspaces.rename.helpNamed',
                        defaultMessage:
                          'Members see this name in their workspace menu and in the subject of workspace emails. Leave it empty to use the default name, {name}.',
                      },
                      { name: defaultName }
                    )
                  : intl.formatMessage({
                      id: 'app.sml.workspaces.rename.help',
                      defaultMessage:
                        'Members see this name in their workspace menu and in the subject of workspace emails. Leave it empty to use the default name: the first artist, or your email.',
                    })}
          </FormText>
          {showSaved && (
            <Alert color="success" className="mt-3 mb-0">
              {intl.formatMessage({ id: 'app.sml.workspaces.rename.saved', defaultMessage: 'Workspace name saved.' })}
            </Alert>
          )}
          {error !== null && error !== undefined && (
            <Alert color="danger" className="mt-3 mb-0">
              {teamErrorMessage(intl, error)}
            </Alert>
          )}
        </Form>
      </PanelBody>
    </Panel>
  );
};

export const WorkspaceNameForm = TypedMemo(WorkspaceNameFormComponent);
